import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProfileService } from '../../modules/shared/services/profile.service';
import {
    profileCheckValidity,
    profileClearCache,
    profileEdit,
    profileFailure,
    profileInitialize,
    profileReceived,
    profileReload,
    profileRequest,
    profileSettingUpdated,
    profileSubmitSetting,
    profileImpersonate,
    profileEndImpersonation, profileSaveAvatar,
} from './profile.actions';
import { catchError, debounceTime, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { Profile } from '../../models/profile';
import { Injectable } from '@angular/core';
import { AppState } from '../index';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { ThemeService } from '../../modules/shared/services/theme.service';
import { SettingsService } from '../../modules/shared/services/settings.service';
import * as Sentry from '@sentry/angular';
import { Clock } from '../../utils/clock';
import { AvatarUploadComponent } from '../../modules/settings/avatar-upload/avatar-upload.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../modules/shared/services/auth.service';
import { ActionService } from '../../modules/shared/services/action.service';
import { selectProfileTimestamp } from './profile.state';

@Injectable()
export class ProfileEffects {

    private journalInitialized = false;

    constructor(private actions$: Actions, private profileService: ProfileService,
                private store: Store<AppState>, private snackbar: MatSnackBar,
                private themeService: ThemeService, private settingsService: SettingsService,
                private dialog: MatDialog, private authService: AuthService,
                private actionService: ActionService) {

        const checkExpiry = () => {
            this.store.dispatch(profileCheckValidity());
            setTimeout(checkExpiry, 60 * 60 * 1000);
        };
        setTimeout(checkExpiry, 60 * 60 * 1000);

    }

    profileInitialize$ = createEffect(() => this.actions$.pipe(
        ofType(profileInitialize),
        map(() => profileRequest({ useCache: true }))
    ));

    profileRequest$ = createEffect(() => this.actions$.pipe(
        ofType(profileRequest),
        mergeMap(action => this.profileService.loadCurrentUser(action.useCache).pipe(
            map((profile: Profile) => profileReceived({ profile })),
            catchError(() => of(profileFailure()))
        ))
    ));

    profileImpersonate$ = createEffect(() => this.actions$.pipe(
        ofType(profileImpersonate),
        mergeMap((action) => this.profileService.beginImpersonation(action.username).pipe(
            map((profile: Profile) => profileReceived({ profile })),
            catchError(() => of(profileFailure()))
        ))
    ));

    profileEndImpersonation$ = createEffect(() => this.actions$.pipe(
        ofType(profileEndImpersonation),
        mergeMap(() => this.profileService.endImpersonation().pipe(
            map((profile: Profile) => profileReceived({ profile })),
            catchError(() => of(profileFailure()))
        ))
    ));

    profileSaveAvatar$ = createEffect(() => this.actions$.pipe(
        ofType(profileSaveAvatar),
        mergeMap((action) => this.profileService.saveAvatar(action.userAvatar).pipe(
            map((profile: Profile) => profileReceived({ profile })),
            catchError(() => of(profileFailure()))
        ))
    ));

    profileReceived$ = createEffect(() => this.actions$.pipe(
        ofType(profileReceived),
        tap((action) => {
            this.profileService.storeProfile(action.profile);
            this.authService.sessionBootstrapped = true;
            Sentry.setUser({ username: action.profile.id });
            if (!this.journalInitialized) {
                this.journalInitialized = true;
                this.actionService.initialize();
            }
        }),
        map(() => profileCheckValidity())
    ));

    profileCheckValidity$ = createEffect(() => this.actions$.pipe(
        ofType(profileCheckValidity),
        withLatestFrom(this.store.select(selectProfileTimestamp)),
        filter(([ , timestamp ]) => !timestamp || Clock.ageInSeconds(timestamp) > 59 * 60),
        map(() => profileReload())
    ));

    profileReload$ = createEffect(() => this.actions$.pipe(
        ofType(profileReload),
        map(() => profileRequest({ useCache: false }))
    ));

    profileClearCache$ = createEffect(() => this.actions$.pipe(
        ofType(profileClearCache),
        tap(() => this.profileService.clearCachedProfile())
    ), { dispatch: false });

    profileFailure$ = createEffect(() => this.actions$.pipe(
        ofType(profileFailure),
        tap(() => {
            if (!this.authService.sessionExpiredInProgress) {
                this.snackbar.open($localize`Please check your internet connection and try again.`);
            }
        })
    ), { dispatch: false });

    profileSettingSubmit$ = createEffect(() => this.actions$.pipe(
        ofType(profileSubmitSetting),
        mergeMap(action => this.settingsService.write(action.id, action.value).pipe(
            map(setting => profileSettingUpdated({ ...setting })),
            catchError(() => of(profileFailure()))
        ))
    ));

    profileSettingUpdated = createEffect(() => this.actions$.pipe(
        ofType(profileSettingUpdated),
        debounceTime(5000),
        map(() => profileReload())
    ));

    profileEdit$ = createEffect(() => this.actions$.pipe(
        ofType(profileEdit),
        tap(() => {
            this.dialog.open(AvatarUploadComponent, { width: '600px', height: '400px' });
        })
    ), { dispatch: false });

}
